/**
 * Console session (ADR-32/33). The access token lives in THIS module's
 * closure only - never storage. It is re-minted from the HttpOnly cookie via
 * POST /auth/refresh, and handed to the ring on every (re)connect.
 *
 * Dev: `?dev=<tenantSlug>:<tenantId>:<role>[,<role>...]&actor=<name>` uses
 * control-api's DevAuth headers and the dev ring's dev:<id>:<role> tokens.
 * Same shape, different proof - exactly the split the servers make.
 *
 * Multi-role (ADR-44): one person wears several hats on sale day. `roles` is
 * every role this principal holds at the tenant; `role` is the ACTIVE one -
 * chosen by `?as=<role>` when it names a held role, else the first held.
 * The active role rides every API call (x-active-staff-role) and the SERVER
 * verifies it against the assured memberships; the browser only requests.
 */

import type { Identity } from '@hipnumber/api-contract/client';

export interface Session {
  readonly mode: 'dev' | 'cognito';
  readonly tenantSlug: string;
  readonly tenantId: string | undefined;
  readonly actorId: string;
  /** The ACTIVE role this session operates as. */
  readonly role: string;
  /** Every role this principal holds at the tenant. */
  readonly roles: readonly string[];
  /** Set ONLY when a platform operator is ASSUMING a tenant role (?actAs=): every
   *  call rides x-act-as-role and the server audits it as `assume`. Undefined for
   *  ordinary staff sessions. */
  readonly actingAs?: string;
  /** API identity for the typed client (bearer or dev headers). */
  apiIdentity(): Identity;
  /** Token for the ring's HELLO - fresh on every call. */
  ringToken(): Promise<string | undefined>;
  signOut(): Promise<void>;
}

/** `?as=` wins when it names a role actually held; else the first held. */
function pickActive(roles: readonly string[], params: URLSearchParams): string {
  const want = params.get('as');
  return want && roles.includes(want) ? want : (roles[0] ?? 'clerk');
}

export class NeedsSignIn extends Error {
  constructor(public readonly signInUrl: string) { super('sign-in required'); this.name = 'NeedsSignIn'; }
}

const CSRF = { 'x-requested-with': 'hipnumber' };

export async function bootSession(opts: { apiBase: string; params: URLSearchParams; signInUrl: string }): Promise<Session> {
  const actAs = opts.params.get('actAs');
  const dev = opts.params.get('dev');
  if (dev) {
    const [tenantSlug, tenantId, rolesRaw = 'clerk'] = dev.split(':');
    if (!tenantSlug || !tenantId) throw new Error('dev=<tenantSlug>:<tenantId>:<role>[,<role>...]');
    const roles = rolesRaw.split(',').filter(Boolean);
    const role = pickActive(roles, opts.params);
    const actorId = opts.params.get('actor') ?? 'clerk-dev';
    return {
      mode: 'dev', tenantSlug, tenantId, actorId, role, roles,
      // devStaff carries EVERY held role; activeRole picks the hat. The
      // server-side DevAuth + active-role check mirror production exactly.
      apiIdentity: () => ({ devStaff: { tenantId, role: roles.join(',') }, activeRole: role }),
      ringToken: async () => `dev:${actorId}:${role}`,
      signOut: async () => undefined,
    };
  }

  // Production: mint an access token from the session cookie.
  let accessToken: string | undefined;
  let expiresAt = 0;
  async function refresh(): Promise<void> {
    const r = await fetch(`${opts.apiBase}/auth/refresh`, { method: 'POST', credentials: 'include', headers: CSRF });
    if (!r.ok) throw new NeedsSignIn(opts.signInUrl);
    const j = (await r.json()) as { accessToken: string; expiresIn: number };
    accessToken = j.accessToken;
    expiresAt = Date.now() + j.expiresIn * 1000;
  }
  await refresh();
  const claims = JSON.parse(atob(accessToken!.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { username?: string; sub: string; 'cognito:groups'?: string[] };

  // Operator act-as (prod command-center hand-off): the booted token carries
  // platform:operator and the caller asked to assume a role (?actAs=). Tenant
  // comes from ?t=; every call rides x-act-as-role - NOT x-active-staff-role,
  // which an operator holds none of, and which the server would 403 as
  // ACTIVE_ROLE_NOT_HELD. Mirrors web-admin's session so the office board is
  // reachable via the same operator hand-off as the React admin.
  const isOperator = (claims['cognito:groups'] ?? []).includes('platform:operator');
  if (actAs && isOperator) {
    const t = opts.params.get('t');
    if (!t) throw new Error('operator act-as requires ?t=<tenantSlug>');
    return {
      mode: 'cognito', tenantSlug: t, tenantId: undefined, actorId: claims.username ?? claims.sub, role: actAs, roles: [actAs], actingAs: actAs,
      apiIdentity: () => ({ bearer: accessToken!, actAsRole: actAs }),
      ringToken: async () => { if (!accessToken || Date.now() > expiresAt - 60_000) await refresh(); return accessToken; },
      signOut: async () => {
        await fetch(`${opts.apiBase}/auth/signout`, { method: 'POST', credentials: 'include', headers: CSRF }).catch(() => undefined);
        accessToken = undefined;
      },
    };
  }

  const tenantSlug = opts.params.get('t');
  if (!tenantSlug) throw new Error('missing ?t=<tenantSlug>');
  // The staff groups name the tenant by id. One person may hold several
  // roles there (ADR-44); we collect them all and operate as one at a time.
  // These are labels for the UI - authority is the server's verification.
  const groups = (claims['cognito:groups'] ?? []).filter((g) => g.startsWith('t:'));
  const tenantId = groups[0]?.split(':')[1];
  const roles = [...new Set(groups.filter((g) => g.split(':')[1] === tenantId).map((g) => g.split(':')[2]).filter((r): r is string => !!r))];
  const role = pickActive(roles, opts.params);
  const fresh = async (): Promise<string | undefined> => {
    if (!accessToken || Date.now() > expiresAt - 60_000) await refresh();
    return accessToken;
  };
  return {
    mode: 'cognito', tenantSlug, tenantId, actorId: claims.username ?? claims.sub, role, roles,
    apiIdentity: () => ({ bearer: accessToken!, ...(tenantId ? { tenantHint: tenantId } : {}), activeRole: role }),
    ringToken: fresh,
    signOut: async () => {
      await fetch(`${opts.apiBase}/auth/signout`, { method: 'POST', credentials: 'include', headers: CSRF }).catch(() => undefined);
      accessToken = undefined;
    },
  };
}
